import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { Box, Heading, List, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { FeatureCard } from '~/components/feature-card';
import { FeatureCardsSection } from '~/components/feature-cards-section';
import { trackEvent } from '~/features/analytics/track-event';
import {
  BookIcon,
  CheckedIcon,
  CircleArrowRightIcon,
  LightBulbIcon,
  RocketIcon,
  SupportIcon,
} from '~/features/experiments/ab_plp_redesign_2025/icons';

export function UdacityPlatformSection() {
  const { t } = useTranslation();
  const features = [
    {
      id: 'skillMeasurement',
      icon: <CheckedIcon height={{ base: '14px', md: '18px' }} width={{ base: '14px', md: '18px' }} />,
      subtitle: t('enterprise.overview.platform.features.skillMeasurement.subtitle'),
      title: t('enterprise.overview.platform.features.skillMeasurement.title'),
      description: t('enterprise.overview.platform.features.skillMeasurement.description'),
      imagePath: '/images/enterprise/ai-and-machine-learning.png',
      theme: 'blue',
      ctaLabel: t('enterprise.overview.platform.features.skillMeasurement.ctaLabel'),
      ctaLink: '/enterprise/overview/skills',
    },
    {
      id: 'analytics',
      icon: <LightBulbIcon height={{ base: '14px', md: '18px' }} width={{ base: '14px', md: '18px' }} />,
      subtitle: t('enterprise.overview.platform.features.analytics.subtitle'),
      title: t('enterprise.overview.platform.features.analytics.title'),
      description: t('enterprise.overview.platform.features.analytics.description'),
      imagePath: '/images/enterprise/analytics-integrations.png',
      theme: 'orange',
    },
    {
      id: 'cohort',
      icon: <SupportIcon height={{ base: '14px', md: '18px' }} width={{ base: '14px', md: '18px' }} />,
      subtitle: t('enterprise.overview.platform.features.cohort.subtitle'),
      title: t('enterprise.overview.platform.features.cohort.title'),
      description: t('enterprise.overview.platform.features.cohort.description'),
      imagePath: '/images/enterprise/cohort-model.png',
      theme: 'cyan',
      ctaLabel: t('enterprise.overview.platform.features.cohort.ctaLabel'),
      ctaLink: '/enterprise/overview/cohorts',
    },
    {
      id: 'projectBased',
      icon: <LightBulbIcon height={{ base: '14px', md: '18px' }} width={{ base: '14px', md: '18px' }} />,
      subtitle: t('enterprise.overview.platform.features.projectBased.subtitle'),
      title: t('enterprise.overview.platform.features.projectBased.title'),
      description: t('enterprise.overview.platform.features.projectBased.description'),
      imagePath: '/images/enterprise/projects.png',
      theme: 'purple',
      ctaLabel: t('enterprise.overview.platform.features.projectBased.ctaLabel'),
      ctaLink: '/enterprise/overview/project-based-learning',
    },
    {
      id: 'humanAi',
      icon: <BookIcon height={{ base: '14px', md: '18px' }} width={{ base: '14px', md: '18px' }} />,
      subtitle: t('enterprise.overview.platform.features.humanAi.subtitle'),
      title: t('enterprise.overview.platform.features.humanAi.title'),
      description: t('enterprise.overview.platform.features.humanAi.description'),
      imagePath: '/images/enterprise/human-ai-support-v2.png',
      theme: 'blue',
      ctaLabel: t('enterprise.overview.platform.features.humanAi.ctaLabel'),
      ctaLink: '/enterprise/overview/human-ai-support',
    },
  ];

  return (
    <FeatureCardsSection
      headerIcon={<RocketIcon color='grey.500' height='18px' width='18px' />}
      headerSubtitle={t('enterprise.overview.platform.subtitle') as string}
      headerDescription={
        <Text color='grey.500' margin='0 auto' maxWidth='844px'>
          {t('enterprise.overview.platform.description')}
        </Text>
      }
      headerTitle={
        <Heading
          as='h2'
          color='grey.900'
          fontSize={{ base: '2.375rem', md: '3.375rem' }}
          fontWeight='600'
          letterSpacing='-1px'
          lineHeight='1.1'
        >
          {t('enterprise.overview.platform.title')}
        </Heading>
      }
    >
      <List display='flex' flexDirection='column' gap={{ base: '32px', md: '56px' }}>
        {features.map((feature, idx) => (
          <FeatureCard
            key={feature.id}
            description={feature.description as string}
            icon={feature.icon}
            isReversed={idx % 2 === 1}
            subtitle={feature.subtitle as string}
            themeColor={`${feature.theme}.700`}
            title={feature.title as string}
            titleProps={{ as: 'p' }}
            containerProps={{
              as: 'li',
              bgColor: `${feature.theme}.50`,
              border: '1px solid',
              borderColor: `${feature.theme}.100`,
              borderRadius: 'lg',
              boxShadow: 'none',
            }}
            ctaSlot={
              feature.ctaLabel && feature.ctaLink ? (
                <ButtonLink
                  linkProps={{ href: feature.ctaLink }}
                  buttonProps={{
                    leftIcon: <CircleArrowRightIcon height='20px' width='20px' />,
                    width: 'fit-content',
                    mt: 'auto',
                    variant: 'outline',
                    color: `${feature.theme}.700`,
                    boxShadow: `inset 0 0 0 1.5px var(--chakra-colors-${feature.theme}-700)`,
                    onClick: () =>
                      trackEvent({
                        name: 'Enterprise CTA Clicked',
                        type: 'click',
                        label: 'platform_feature_cta_clicked',
                        value: feature.id,
                      }),
                  }}
                >
                  {feature.ctaLabel}
                </ButtonLink>
              ) : null
            }
            imageSlot={
              <Box
                flex={{ md: '1 1 352px' }}
                position='relative'
                alignSelf={{
                  base: 'stretch',
                  md: 'center',
                }}
              >
                <Image
                  alt=''
                  height={352}
                  loading='lazy'
                  src={feature.imagePath}
                  style={{ width: '100%', height: 'auto' }}
                  width={524}
                />
              </Box>
            }
          />
        ))}
      </List>
    </FeatureCardsSection>
  );
}
